import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube, MapPin, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { PaymentLogos } from './PaymentLogos';
import { Language } from '../translations';
import { getLocalizedPath } from '../lib/i18n';

interface FooterProps {
    t: any;
    homeSections?: any[];
    lang?: Language;
}

const RESCUE_TITLES: Record<Language, string> = {
    es: 'ASEGURA TU EXPEDICIÓN',
    en: 'INSURE YOUR EXPEDITION',
    pt: 'ASSEGURE SUA EXPEDIÇÃO',
    fr: 'ASSUREZ VOTRE EXPÉDITION',
    it: 'ASSICURA LA TUA SPEDIZIONE',
    de: 'SICHERE DEINE EXPEDITION AB'
};

const SOCIAL_TITLES: Record<Language, string> = {
    es: 'REDES SOCIALES',
    en: 'SOCIAL MEDIA',
    pt: 'REDES SOCIAIS',
    fr: 'RÉSEAUX SOCIAUX',
    it: 'SOCIAL MEDIA',
    de: 'SOZIALE MEDIEN'
};

const Footer: React.FC<FooterProps> = ({ t, homeSections, lang = 'es' }) => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    const rescueTitle = t?.footer_rescue_title || RESCUE_TITLES[lang] || RESCUE_TITLES.es;
    const socialTitle = t?.footer_social || SOCIAL_TITLES[lang] || SOCIAL_TITLES.es;

    // Extract dynamic footer section if exists
    const footerSec = homeSections?.find(s => s.id === 'footer');
    const localizedContent = footerSec?.[`content_${lang}`];
    const hasLocalizedContent = localizedContent && typeof localizedContent === 'object' && Object.keys(localizedContent).length > 0;
    
    // In Spanish, prioritize DB content_es. In other languages, do not show Spanish DB content when English is missing.
    const footerContent = (lang === 'es')
        ? (footerSec?.content_es || {})
        : (hasLocalizedContent ? localizedContent : {});

    const footerManifesto = footerContent.manifesto || (lang === 'es' ? footerSec?.content_es?.manifesto : null) || t.featured_text;
    const footerLocation = footerContent.location || (lang === 'es' ? footerSec?.content_es?.location : null) || t.footer_location;
    const rawEmail = footerContent.email || footerSec?.content_es?.email || 'hello@fisuraexpeditions.com';
    const footerEmail = (rawEmail && !rawEmail.includes('patagoniaclimbing')) ? rawEmail : 'hello@fisuraexpeditions.com';

    const handleSubscribe = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setStatus('loading');
        try {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://iuixlsqrlfkfnvnckgij.supabase.co';
            const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
            
            const response = await fetch(`${supabaseUrl}/functions/v1/subscribe-newsletter`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${anonKey}`
                },
                body: JSON.stringify({ email })
            });

            if (response.ok) {
                setStatus('success');
                setEmail('');
            } else {
                const errData = await response.json().catch(() => ({}));
                console.error('Subscription API failure:', errData);
                setStatus('error');
            }
        } catch (err) {
            console.error('Subscription request error:', err);
            setStatus('error');
        } finally {
            setTimeout(() => setStatus('idle'), 5000);
        }
    };

    return (
        <footer className="bg-[#050505] pt-32 pb-16 px-6 border-t border-rock selection:bg-[#00d4ff] selection:text-black">
            <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-32">
                    {/* Column 1: Brand & Contact */}
                    <div className="space-y-8">
                        <div>
                            <img 
                                src="/logo-fisura-white.png" 
                                alt="Fisura Expeditions" 
                                className="h-14 md:h-16 w-auto object-contain"
                            />
                        </div>
                        <p className="text-[13px] text-gray-400 font-light leading-relaxed uppercase tracking-[0.15em] max-w-sm">
                            {footerManifesto}
                        </p>
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 text-gray-300 leading-relaxed italic border-l border-[#00d4ff] pl-4 text-[11px] sm:text-xs uppercase tracking-[0.15em]">
                                <MapPin size={14} className="text-[#00d4ff] mt-1 shrink-0" />
                                <span>{footerLocation}</span>
                            </div>
                            <div className="pl-4 text-[11px] sm:text-xs font-medium normal-case tracking-wider break-all">
                                <a 
                                    href={`mailto:${footerEmail}`} 
                                    className="text-gray-300 hover:text-[#00d4ff] transition-colors"
                                >
                                    {footerEmail}
                                </a>
                            </div>
                        </div>

                        {/* ACCREDITATIONS & SEALS (SERNATUR & TURISMO SUSTENTABLE) */}
                        <div className="flex items-start gap-6 pt-2 flex-wrap sm:flex-nowrap">
                            {/* SERNATUR QR Code */}
                            <a
                                href="https://registro.sernatur.cl/registroempresarios/fichaservicio?sid=90240&sign=3b195eafd6db271279ff0f03457301f0"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    console.log("🔍 Sernatur badge clicked! Sending GA4 event click_sernatur...");
                                    if (typeof window.gtag === 'function') {
                                        window.gtag('event', 'click_sernatur', {
                                            location: 'footer_badge'
                                        });
                                    }
                                }}
                                className="group inline-flex flex-col items-start gap-3"
                                aria-label="Ver ficha oficial SERNATUR"
                            >
                                <div className="relative p-2.5 bg-white rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(0,212,255,0.3)] transition-all duration-300 group-hover:scale-[1.03]">
                                    <QRCodeSVG
                                        value="https://registro.sernatur.cl/registroempresarios/fichaservicio?sid=90240&sign=3b195eafd6db271279ff0f03457301f0"
                                        size={88}
                                        bgColor="#ffffff"
                                        fgColor="#0a0a0a"
                                        level="M"
                                    />
                                    <div className="absolute -bottom-1.5 -right-1.5 bg-[#00d4ff] text-black text-[7px] font-black uppercase tracking-[0.15em] px-1.5 py-0.5 rounded">
                                        SERNATUR
                                    </div>
                                </div>
                                <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 group-hover:text-[#00d4ff] transition-colors leading-relaxed">
                                    {t?.sernatur_official || (lang === 'en' ? 'Official Registry' : 'Registro Oficial')}<br />
                                    {t?.sernatur_provider || (lang === 'en' ? 'Tour Operator' : 'Prestador de Servicios')}
                                </p>
                            </a>

                            {/* TURISMO SUSTENTABLE BADGE */}
                            <a
                                href="https://sustentabilidad.sernatur.cl/"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    if (typeof (window as any).gtag === 'function') {
                                        (window as any).gtag('event', 'click_turismo_sustentable', {
                                            location: 'footer_badge'
                                        });
                                    }
                                }}
                                className="group inline-flex flex-col items-start gap-3"
                                aria-label="Compromiso Turismo Sustentable - Buenas Prácticas"
                                title="Compromiso Turismo Sustentable - Sernatur Chile"
                            >
                                <div className="relative h-[108px] w-[108px] flex items-center justify-center p-0.5 group-hover:scale-[1.03] transition-all duration-300">
                                    <img
                                        src="/logo-compromiso-sustentable.png"
                                        alt="Compromiso Turismo Sustentable - Buenas Prácticas"
                                        className="h-full w-full object-contain filter drop-shadow-[0_4px_12px_rgba(0,154,68,0.25)] group-hover:drop-shadow-[0_0_20px_rgba(0,154,68,0.5)] transition-all duration-300"
                                    />
                                </div>
                                <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 group-hover:text-[#00d4ff] transition-colors leading-relaxed">
                                    {t?.sustainability_title || (lang === 'en' ? 'Sustainable Tourism' : lang === 'pt' ? 'Turismo Sustentável' : 'Turismo Sustentable')}<br />
                                    {t?.sustainability_subtitle || (lang === 'en' ? 'Good Practices' : lang === 'pt' ? 'Boas Práticas' : 'Buenas Prácticas')}
                                </p>
                            </a>
                        </div>
                        <div className="pt-1">
                            <Link
                                to={getLocalizedPath('/certificaciones', lang)}
                                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#00d4ff]/80 hover:text-[#00d4ff] transition-colors group"
                            >
                                <span>{t.certifications_all || (lang === 'en' ? 'View official certifications & badges' : 'Ver certificaciones y reconocimientos')}</span>
                                <span className="group-hover:translate-x-1 transition-transform">→</span>
                            </Link>
                        </div>
                    </div>

                    {/* Column 2: Navigation */}
                    <div className="space-y-8 lg:pl-8">
                        <h3 className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-200 border-b border-white/10 pb-4">
                            {t.footer_explore || (lang === 'en' ? 'EXPLORE' : 'RUTAS')}
                        </h3>
                        <ul className="space-y-4 text-[13px] font-bold uppercase tracking-[0.2em]">
                            <li><Link to={getLocalizedPath('/', lang)} className="text-gray-400 hover:text-white transition-colors">{t.nav_home}</Link></li>
                            <li><Link to={getLocalizedPath('/expeditions', lang)} className="text-gray-400 hover:text-white transition-colors">{t.nav_expeditions}</Link></li>
                            <li><Link to={getLocalizedPath('/about', lang)} className="text-gray-400 hover:text-white transition-colors">{t.nav_about}</Link></li>
                            <li><Link to={getLocalizedPath('/certificaciones', lang)} className="text-gray-400 hover:text-white transition-colors">{t.nav_certifications || 'Certificaciones'}</Link></li>
                            <li><Link to={getLocalizedPath('/blog', lang)} className="text-gray-400 hover:text-white transition-colors">{t.nav_blog || 'Blog'}</Link></li>
                            <li><Link to={getLocalizedPath('/terms', lang)} className="text-gray-400 hover:text-white transition-colors">{t.terms_title}</Link></li>
                        </ul>

                        {/* Global Rescue Partner */}
                        <div className="pt-[21px]">
                            <a
                                href="https://www.globalrescue.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                    if (typeof (window as any).gtag === 'function') {
                                        (window as any).gtag('event', 'click_global_rescue', {
                                            location: 'footer'
                                        });
                                    }
                                }}
                                className="group inline-flex flex-col items-start gap-3"
                                aria-label={`Global Rescue - ${rescueTitle}`}
                            >
                                <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-gray-400 group-hover:text-[#00d4ff] transition-colors">
                                    {rescueTitle}
                                </h4>
                                <div className="relative inline-block overflow-hidden">
                                    <img
                                        src="/global-rescue-white-red.png"
                                        alt="Global Rescue"
                                        className="h-7 sm:h-8 w-auto object-contain block opacity-95 group-hover:opacity-100 transition-opacity duration-300"
                                    />
                                    {/* Brillito lateral en hover */}
                                    <div 
                                        className="absolute inset-0 pointer-events-none overflow-hidden"
                                        style={{
                                            WebkitMaskImage: "url('/global-rescue-white-red.png')",
                                            maskImage: "url('/global-rescue-white-red.png')",
                                            WebkitMaskSize: "contain",
                                            maskSize: "contain",
                                            WebkitMaskRepeat: "no-repeat",
                                            maskRepeat: "no-repeat",
                                        }}
                                    >
                                        <div className="shine-effect w-1/3 h-full bg-gradient-to-r from-transparent via-white/80 to-transparent pointer-events-none" />
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>


                    {/* Column 4: Newsletter & Social Bar */}
                    <div className="space-y-8">
                        <h3 className="text-[10px] font-bold uppercase tracking-[0.4em] text-gray-200 border-b border-white/10 pb-4">
                            {t.footer_newsletter_title}
                        </h3>
                        <div className="space-y-4">
                            <p className="text-xs text-gray-400 leading-relaxed font-light uppercase tracking-wider">
                                {t.footer_newsletter_desc}
                            </p>
                            <form onSubmit={handleSubscribe} className="relative flex items-center">
                                <input 
                                    type="email" 
                                    required
                                    placeholder={t.footer_newsletter_placeholder}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={status === 'loading' || status === 'success'}
                                    className="w-full bg-white/5 border border-white/10 rounded px-4 py-2.5 pr-12 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#00d4ff] focus:ring-1 focus:ring-[#00d4ff] disabled:opacity-50 transition-all font-light tracking-wide uppercase"
                                />
                                <button 
                                    type="submit"
                                    disabled={status === 'loading' || status === 'success'}
                                    className="absolute right-1 top-1 bottom-1 px-3 bg-[#00d4ff] hover:bg-white text-black transition-colors rounded flex items-center justify-center disabled:opacity-50 disabled:bg-[#00d4ff]"
                                    aria-label="Subscribe"
                                >
                                    {status === 'loading' ? (
                                        <div className="w-3.5 h-3.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <Send size={12} />
                                    )}
                                </button>
                            </form>
                            {status === 'success' && (
                                <p className="text-[10px] font-bold text-[#00d4ff] tracking-[0.1em] uppercase animate-pulse">
                                    {t.newsletter_success}
                                </p>
                            )}
                            {status === 'error' && (
                                <p className="text-[10px] font-bold text-red-500 tracking-[0.1em] uppercase animate-pulse">
                                    {t.newsletter_error}
                                </p>
                            )}
                            <div className="pt-6 space-y-4">
                                <h4 className="text-[9px] font-bold uppercase tracking-[0.3em] text-gray-400">
                                    {socialTitle}
                                </h4>
                                <div className="flex items-center gap-6">
                                    <a 
                                        href="https://instagram.com/fisuraexpeditions" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-gray-400 hover:text-[#00d4ff] hover:scale-110 transition-all duration-300 shrink-0"
                                        aria-label="Instagram"
                                    >
                                        <Instagram size={20} />
                                    </a>
                                    <a 
                                        href="https://wa.me/56967273815" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        onClick={() => {
                                            console.log("🔍 WhatsApp footer button clicked! Sending GA4 event click_whatsapp...");
                                            if (typeof window.gtag === 'function') {
                                                window.gtag('event', 'click_whatsapp', {
                                                    location: 'footer'
                                                });
                                            }
                                        }}
                                        className="text-gray-400 hover:text-[#00d4ff] hover:scale-110 transition-all duration-300 shrink-0"
                                        aria-label="WhatsApp"
                                    >
                                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.46h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                        </svg>
                                    </a>
                                    <a 
                                        href="https://youtube.com" 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="text-gray-400 hover:text-[#00d4ff] hover:scale-110 transition-all duration-300 shrink-0"
                                        aria-label="YouTube"
                                    >
                                        <Youtube size={20} />
                                    </a>
                                </div>
                            </div>

                            {/* Payment Methods */}
                            <PaymentLogos lang={lang} title={t?.footer_payment_methods} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-8 text-[9px] uppercase tracking-[0.3em] font-bold text-gray-600 md:pr-20">
                <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                    <p className="text-gray-500">
                        © 2026 FISURA EXPEDITIONS — BORN IN THE GRANITE.
                    </p>
                    {/* Hidden for now */}
                    <a
                        href="https://alali.cl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="opacity-0 pointer-events-none"
                    >
                        Sitio creado por Alali Tech Group
                    </a>
                </div>
                <div className="flex items-center gap-8 text-gray-600">
                    <span className="font-mono">AYSÉN, CHILE</span>
                    <span className="w-12 h-px bg-white/10"></span>
                    <span className="font-mono">LAT: -46.1264 LON: -72.1648</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
